import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { useGetPredictionCategoryTree } from '@/api/prediction.queries';
import type { ForecastTraderCategoryWithId } from '@/types/forecasttrader.types';
import { usePredictionList } from './usePredictionHub';

/**
 * Composable for getting top-level categories
 * Filters categories to exclude those with parent_id and specifically excludes 'g2'
 */
export const useGetTopLevelCategories = (enabled: MaybeRefOrGetter<boolean> = true) => {
  const { data: categoryTreeData, isLoading, error } = useGetPredictionCategoryTree(enabled);

  const topLevelCategories = computed(() => {
    if (!categoryTreeData.value?.categories) {
      return [];
    }

    const result: ForecastTraderCategoryWithId[] = [];

    for (const category of categoryTreeData.value.categories) {
      if (!category.parent_id && category.id !== 'g2') {
        result.push({
          name: category.name,
          parent_id: category.parent_id,
          id: category.id,
          restricted: category.restricted,
        });
      }
    }

    return result;
  });

  return {
    data: topLevelCategories,
    isLoading,
    error,
  };
};

/**
 * Composable for getting subcategories of a specific parent category
 */
export const useGetSubcategories = (categoryId: MaybeRefOrGetter<string>) => {
  const { data: categoryTreeData, isLoading, error } = useGetPredictionCategoryTree();

  const categories = computed(() => {
    const parentId = toValue(categoryId);

    if (!categoryTreeData.value?.categories) {
      return [];
    }

    const result: ForecastTraderCategoryWithId[] = [];

    for (const category of categoryTreeData.value.categories) {
      if (category.parent_id === parentId) {
        result.push({
          name: category.name,
          parent_id: category.parent_id,
          id: category.id,
          restricted: category.restricted,
        });
      }
    }

    return result.sort((a, b) => a.name.localeCompare(b.name));
  });

  return {
    data: categories,
    isLoading,
    error,
  };
};

/**
 * Composable for getting markets for a specific category.
 *
 * Sends the single selected category id to the prediction list API and lets
 * the server decide how to aggregate descendants. Previously this composable
 * client-side fanned the id into `[parent, ...children, ...grandchildren]`
 * and joined them as a comma-separated list, which caused the real API to
 * return the same product once per matching category — surfacing as duplicate
 * cards in the home page sliders. This matches desktop `MarketsPage`'s behavior.
 */
export const useGetCategoryMarkets = (categoryId: MaybeRefOrGetter<string>) => {
  const categoriesParam = computed(() => toValue(categoryId) || '');

  const listEnabled = computed(() => !!categoriesParam.value);
  const {
    data: markets,
    isLoading: productsLoading,
    error: productsError,
  } = usePredictionList(categoriesParam, undefined, listEnabled, () => toValue(categoryId) || undefined);

  return {
    data: markets,
    isLoading: productsLoading,
    error: productsError,
  };
};

/**
 * Composable for getting category name by category ID
 */
export const useGetCategoryName = (categoryId: MaybeRefOrGetter<string>) => {
  const { data: categoryTreeData, isLoading, error } = useGetPredictionCategoryTree();

  const categoryName = computed(() => {
    const currentCategoryId = toValue(categoryId);

    if (!categoryTreeData.value?.categories || !currentCategoryId) {
      return undefined;
    }

    const category = categoryTreeData.value.categories.find((c) => c.id === currentCategoryId);
    return category?.name;
  });

  return {
    data: categoryName,
    isLoading,
    error,
  };
};

/**
 * Resolve the top-level (L1) ancestor of a leaf category by walking up the
 * `parent_id` chain in the category tree. Returns the leaf id itself if it is
 * already top-level, or '' when the id is unknown / tree not yet loaded.
 */
export const useGetTopLevelCategoryId = (categoryId: MaybeRefOrGetter<string>) => {
  const { data: categoryTreeData } = useGetPredictionCategoryTree();

  const topLevelId = computed(() => {
    const leafId = toValue(categoryId);
    const categories = categoryTreeData.value?.categories;
    if (!leafId || !categories) return '';

    const byId = new Map(categories.map((c) => [c.id, c]));
    let current: string | undefined = leafId;
    while (current) {
      const cat = byId.get(current);
      if (!cat?.parent_id) return current;
      current = cat.parent_id;
    }
    return '';
  });

  return { data: topLevelId };
};

/**
 * Build the ordered breadcrumb path (root → leaf) for a category by walking up
 * the `parent_id` chain in the category tree. Returns `[]` until the tree loads
 * or when the id is unknown. Used by the product-details header breadcrumb.
 */
export const useGetCategoryPath = (categoryId: MaybeRefOrGetter<string>) => {
  const { data: categoryTreeData } = useGetPredictionCategoryTree();

  const path = computed<ForecastTraderCategoryWithId[]>(() => {
    const leafId = toValue(categoryId);
    const categories = categoryTreeData.value?.categories;
    if (!leafId || !categories) return [];

    const byId = new Map(categories.map((c) => [c.id, c]));
    const chain: ForecastTraderCategoryWithId[] = [];
    let current: string | undefined = leafId;
    // Guard against malformed (cyclic) trees with a visited set.
    const seen = new Set<string>();
    while (current && !seen.has(current)) {
      seen.add(current);
      const cat = byId.get(current);
      if (!cat) break;
      chain.unshift({ name: cat.name, parent_id: cat.parent_id, id: cat.id, restricted: cat.restricted });
      current = cat.parent_id;
    }
    return chain;
  });

  return { data: path };
};

export const useGetRelatedCategoryProducts = (
  categoryId: MaybeRefOrGetter<string>,
  excludeConid?: MaybeRefOrGetter<number>,
) => {
  // `contract/list` returns the product's level-3 category. Query that exact
  // leaf through `product/list` so Related stays within the same market group
  // instead of widening to every descendant of its level-2 parent.
  const categoriesParam = computed(() => toValue(categoryId) || '');

  const listEnabled = computed(() => !!categoriesParam.value);
  const {
    data: allMarkets,
    isLoading: productsLoading,
    error: productsError,
  } = usePredictionList(categoriesParam, undefined, listEnabled, () => toValue(categoryId) || undefined);

  const markets = computed(() => {
    const conidToExclude = excludeConid ? toValue(excludeConid) : 0;
    if (!conidToExclude) return allMarkets.value;
    return allMarkets.value.filter((m) => m.conid !== conidToExclude);
  });

  return {
    data: markets,
    isLoading: productsLoading,
    error: productsError,
  };
};
// last modified by AI tools on 2026-10-09
